import { useMemo, useState } from 'react';
import { Table, Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { reposService } from '@/services/api';
import type { RepoSeveritySummary } from '@/services/api/repos-service';
import { useAsync } from '@/common/hooks/use-async';
import { AsyncBoundary } from '@/common/components/async-boundary';
import {
  SEVERITY_ORDER,
  SEVERITY_BADGE_BG,
  EMPTY_COUNTS,
  severityScore,
  type SeverityCounts,
  type Severity,
} from '@/common/utilities/severity';

/** Columns you can sort by. `vulns` = weighted severity score (needs-attention ranking). */
type SortKey = 'repo' | 'vulns' | Severity;
type SortDir = 'asc' | 'desc';

interface RepoView {
  repo: string;
  enabled: boolean;
  owners: string[];
  counts: SeverityCounts;
  score: number;
}

export default function ReposList() {
  const repos = useAsync(() => reposService.listRepos(), []);
  // Severity counts load independently so the table renders immediately; counts fill in
  // when ready (a scan of the findings table, slower than the small repos scan).
  const summary = useAsync(() => reposService.severitySummary(), []);

  // Sort by severity score descending by default — the repo that needs attention is on top.
  const [sortKey, setSortKey] = useState<SortKey>('vulns');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows: RepoView[] = useMemo(() => {
    const byRepo = new Map<string, RepoSeveritySummary>();
    for (const s of summary.data?.items ?? []) byRepo.set(s.repo, s);
    const views = (repos.data?.items ?? []).map((r) => {
      const s = byRepo.get(r.repo);
      const counts: SeverityCounts = s
        ? { CRITICAL: s.CRITICAL, HIGH: s.HIGH, MEDIUM: s.MEDIUM, LOW: s.LOW }
        : { ...EMPTY_COUNTS };
      return {
        repo: r.repo,
        enabled: Boolean(r.enabled),
        owners: r.owners ?? [],
        counts,
        score: severityScore(counts),
      };
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    views.sort((a, b) => {
      let cmp: number;
      if (sortKey === 'repo') cmp = a.repo.localeCompare(b.repo);
      else if (sortKey === 'vulns') cmp = a.score - b.score;
      else cmp = a.counts[sortKey] - b.counts[sortKey];
      // Stable tiebreak: higher score, then name, so equal rows order predictably.
      if (cmp === 0) cmp = a.score - b.score || b.repo.localeCompare(a.repo);
      return cmp * dir;
    });
    return views;
  }, [repos.data, summary.data, sortKey, sortDir]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Severity/vuln columns default to desc (most first); name defaults to asc.
      setSortDir(key === 'repo' ? 'asc' : 'desc');
    }
  };

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const th = (key: SortKey, label: string, extra?: string) => (
    <th
      role="button"
      className={extra}
      onClick={() => onSort(key)}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
    >
      {label}
      {arrow(key)}
    </th>
  );

  return (
    <Card>
      <Card.Header>Repos ({repos.data?.items.length ?? 0})</Card.Header>
      <Card.Body>
        <AsyncBoundary loading={repos.loading} error={repos.error}>
          <Table hover responsive className="align-middle">
            <thead>
              <tr>
                {th('repo', 'Repo')}
                <th>Enabled</th>
                {SEVERITY_ORDER.map((sev) => th(sev, sevLabel(sev), 'text-center'))}
                {th('vulns', 'Total', 'text-center')}
                <th>Owners</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.repo}>
                  <td>
                    <Link to={`/repos/detail?repo=${encodeURIComponent(r.repo)}&tab=findings`}>
                      {r.repo}
                    </Link>
                  </td>
                  <td>{r.enabled ? 'yes' : 'no'}</td>
                  {SEVERITY_ORDER.map((sev) => (
                    <td key={sev} className="text-center">
                      <SevCell sev={sev} n={r.counts[sev]} loading={summary.loading} />
                    </td>
                  ))}
                  <td className="text-center fw-semibold">
                    {summary.loading
                      ? '…'
                      : r.counts.CRITICAL + r.counts.HIGH + r.counts.MEDIUM + r.counts.LOW}
                  </td>
                  <td>{r.owners.join(', ') || '-'}</td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={8} className="text-muted">
                    No repos.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {summary.error && (
            <div className="text-muted small">
              Vulnerability counts unavailable ({summary.error}).
            </div>
          )}
        </AsyncBoundary>
      </Card.Body>
    </Card>
  );
}

/** Short column header per severity (full word is long; keep the table compact). */
function sevLabel(sev: Severity): string {
  return { CRITICAL: 'Crit', HIGH: 'High', MEDIUM: 'Med', LOW: 'Low' }[sev];
}

/** A severity count cell: a colored badge when > 0, a muted dash when zero. */
function SevCell({ sev, n, loading }: { sev: Severity; n: number; loading: boolean }) {
  if (loading) return <span className="text-muted">…</span>;
  if (!n) return <span className="text-muted">·</span>;
  return <Badge bg={SEVERITY_BADGE_BG[sev]}>{n}</Badge>;
}
